import { Component } from '@theme/component';

/**
 * Fills the line connecting the "How it works" steps as the section scrolls through
 * the viewport, and marks each step active once the fill reaches its marker.
 * Horizontal on wide screens, vertical when stacked. Without JavaScript, or with
 * reduced motion, every step is shown as complete.
 *
 * @typedef {object} ProcessStepsRefs
 * @property {HTMLElement} track - The line behind the step markers
 * @property {HTMLElement[]} [steps] - The step items
 *
 * @extends {Component<ProcessStepsRefs>}
 */
class ProcessStepsComponent extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #frame = 0;
  #isNearViewport = false;

  connectedCallback() {
    super.connectedCallback();
    this.#syncStepCount();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.#isNearViewport = Boolean(entry?.isIntersecting);
        if (this.#isNearViewport) this.#requestUpdate();
      },
      { rootMargin: '25% 0px' }
    );
    this.#observer.observe(this);

    window.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    window.addEventListener('resize', this.#requestUpdate, { signal });

    this.dataset.progressReady = 'true';
    this.#update();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncStepCount();
    if (this.dataset.progressReady) this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  /** Lets the CSS position the connecting line between the first and last markers. */
  #syncStepCount() {
    this.style.setProperty('--steps', String(Math.max(this.refs.steps?.length ?? 0, 1)));
  }

  #handleScroll = () => {
    if (this.#isNearViewport) this.#requestUpdate();
  };

  #requestUpdate = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update() {
    const { track, steps = [] } = this.refs;
    const viewportHeight = window.innerHeight;
    const isVertical = getComputedStyle(this).getPropertyValue('--process-direction').trim() === 'vertical';
    const isRtl = getComputedStyle(this).direction === 'rtl';

    if (isVertical) {
      const lastMarker = steps.at(-1)?.querySelector('.process-step__marker');
      if (lastMarker) {
        const markerRect = lastMarker.getBoundingClientRect();
        const end = this.getBoundingClientRect().bottom - (markerRect.top + markerRect.height / 2);
        this.style.setProperty('--track-end', `${Math.max(end, 0)}px`);
      }
    }

    const trackRect = track.getBoundingClientRect();
    const progress = isVertical
      ? (viewportHeight * 0.65 - trackRect.top) / Math.max(trackRect.height, 1)
      : (viewportHeight * 0.85 - this.getBoundingClientRect().top) / (viewportHeight * 0.5);
    const clamped = Math.min(Math.max(progress, 0), 1);

    this.style.setProperty('--progress', clamped.toFixed(3));

    for (const step of steps) {
      const marker = step.querySelector('.process-step__marker');
      if (!marker) continue;
      const rect = marker.getBoundingClientRect();
      let position;
      if (isVertical) {
        position = (rect.top + rect.height / 2 - trackRect.top) / Math.max(trackRect.height, 1);
      } else if (isRtl) {
        position = (trackRect.right - (rect.left + rect.width / 2)) / Math.max(trackRect.width, 1);
      } else {
        position = (rect.left + rect.width / 2 - trackRect.left) / Math.max(trackRect.width, 1);
      }
      step.classList.toggle('is-active', clamped >= position - 0.02);
    }
  }
}

if (!customElements.get('process-steps')) {
  customElements.define('process-steps', ProcessStepsComponent);
}
